import styled, { keyframes } from "styled-components";

// ============ Animations ============

const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translateY(16px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from { 
    opacity: 0;
    transform: scale(0.95);
  }
  to { 
    opacity: 1;
    transform: scale(1);
  }
`;

// ============ Main Page Wrapper ============

export const SignPageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0e1a 0%, #1a1f35 25%, #0f1424 50%, #1a1f35 75%, #0a0e1a 100%);
  position: relative;
  overflow: hidden;
`;

export const PageContent = styled.div`
  padding: 100px 20px 40px; /* Added top padding for fixed nav */
  min-height: 100vh;
  position: relative;
  z-index: 1;

  /* Grid pattern on dark background - smaller squares */
  background-image: linear-gradient(
      rgba(31, 41, 55, 0.5) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(31, 41, 55, 0.5) 1px, transparent 1px);
  background-size: 20px 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 24px 16px;
  }
`;

export const DecorativeShape = styled.div`
  display: none;
`;

// ============ Loading State ============

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
`;

export const SpinnerLarge = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  border-top-color: #3b82f6;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LoadingText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9375rem;
`;

// ============ Error Card ============

export const ErrorCard = styled.div`
  background: #f8fafc;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 40px;
  text-align: center;
  max-width: 400px;
  margin: 80px auto 0;
  animation: ${scaleIn} 0.3s ease;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const ErrorIcon = styled.div`
  width: 56px;
  height: 56px;
  background: ${({ theme }) => theme.colors.dangerLight};
  color: ${({ theme }) => theme.colors.danger};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
  margin: 0 auto 20px;
`;

// ============ Success Card ============

export const SuccessCard = styled.div`
  background: #f8fafc;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 40px;
  text-align: center;
  max-width: 440px;
  margin: 80px auto 0;
  animation: ${scaleIn} 0.3s ease;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const SuccessIcon = styled.div`
  width: 56px;
  height: 56px;
  background: ${({ theme }) => theme.colors.successLight};
  color: ${({ theme }) => theme.colors.success};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 20px;
`;

// ============ Phone Verification ============

export const VerificationContainer = styled.div`
  max-width: 440px;
  margin: 0 auto;
  padding-top: 60px;
  animation: ${slideUp} 0.4s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-top: 32px;
  }
`;

export const VerificationHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

export const Logo = styled.img`
  height: 40px;
  margin-bottom: 24px;
`;

export const HeaderTitle = styled.h1`
  font-size: 1.375rem;
  color: #ffffff;
  margin-bottom: 4px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

export const HeaderSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9375rem;
  margin: 0;
`;

export const VerificationCard = styled.div`
  background: #f8fafc;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 32px;
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};

  h2 {
    font-size: 1.25rem;
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 4px;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 24px 20px;
  }
`;

export const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 24px;
  font-size: 0.9375rem;
  line-height: 1.5;
`;

export const PhoneInputWrapper = styled.div`
  position: relative;
`;

export const PhonePrefix = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 1rem;
`;

export const PhoneInput = styled.input`
  width: 100%;
  padding: 14px 16px 14px 60px;
  font-size: 1rem;
  letter-spacing: 0.5px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  font-family: inherit;
  direction: ltr;
  text-align: right;
  background: #f8fafc;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textPlaceholder};
  }
`;

export const PhoneMaskedText = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 20px;
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

// ============ OTP Input ============

export const OtpContainer = styled.div`
  margin-top: 24px;
`;

export const OtpInputs = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  direction: ltr;
`;

export const OtpInput = styled.input`
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  border: 1px solid
    ${({ $filled, theme }) =>
      $filled ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ $filled, theme }) =>
    $filled ? theme.colors.accentLight : "white"};
  color: ${({ theme }) => theme.colors.text};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 44px;
    height: 52px;
    font-size: 1.25rem;
  }
`;

export const ResendLink = styled.div`
  text-align: center;
  margin-top: 20px;

  button {
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    font-size: 0.875rem;
    font-family: inherit;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    transition: color ${({ theme }) => theme.transitions.fast};

    &:hover:not(:disabled) {
      text-decoration: underline;
    }

    &:disabled {
      color: ${({ theme }) => theme.colors.textMuted};
      cursor: not-allowed;
    }
  }
`;

// ============ Contract View ============

export const ContractContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1000px;
  margin: 0 auto;
  animation: ${slideUp} 0.4s ease;
`;

export const ContractHeader = styled.div`
  text-align: center;
  margin-bottom: 28px;

  h1 {
    font-size: 1.5rem;
    color: #ffffff;
    margin-bottom: 4px;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9375rem;
    margin: 0;
  }
`;

export const TimeRemainingBadge = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  padding: 8px 16px;
  margin-bottom: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8125rem;

  .icon {
    font-size: 0.9rem;
  }

  .time {
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: #ffffff;
  }

  &.warning {
    background: rgba(245, 158, 11, 0.15);
    border-color: rgba(245, 158, 11, 0.3);
    color: #fcd34d;

    .time {
      color: #fcd34d;
    }
  }

  &.danger {
    background: rgba(239, 68, 68, 0.15);
    border-color: rgba(239, 68, 68, 0.3);
    color: #fca5a5;

    .time {
      color: #fca5a5;
    }
  }
`;

export const SummaryBox = styled.div`
  background: #f8fafc;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

export const SummaryItem = styled.div`
  text-align: center;
  padding: 12px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

export const SummaryLabel = styled.span`
  display: block;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

export const SummaryValue = styled.span`
  display: block;
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

export const ScrollHint = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
  font-size: 0.875rem;
`;

export const ContractContentWrapper = styled.div`
  background: #f8fafc;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const ContractContent = styled.div`
  max-height: 65vh;
  overflow-y: auto;
  padding: 32px;
  direction: rtl;
  line-height: 1.7;

  h1,
  h2,
  h3 {
    color: ${({ theme }) => theme.colors.text};
    margin-top: 20px;
  }

  h1 {
    font-size: 1.375rem;
  }
  h2 {
    font-size: 1.125rem;
  }
  h3 {
    font-size: 1rem;
  }

  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: 14px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 14px 0;
  }

  td,
  th {
    padding: 10px 14px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    text-align: right;
  }

  ul,
  ol {
    margin: 14px 0;
    padding-right: 24px;
  }

  li {
    margin-bottom: 8px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px;
    max-height: 55vh;
  }
`;

// ============ Signing Section ============

export const SigningSection = styled.div`
  padding: 28px 32px;
  background: ${({ theme }) => theme.colors.background};
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px;
  }
`;

export const CheckboxesContainer = styled.div`
  margin-bottom: 24px;
  background: rgba(21, 97, 237, 0.06);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 20px;
  border: 1px solid rgba(21, 97, 237, 0.1);
`;

export const CheckboxesTitle = styled.h3`
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: 16px;
  color: #1561ed;
`;

export const OptionalTitle = styled.h4`
  margin-top: 20px;
  margin-bottom: 12px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: ${({ theme }) => theme.fontWeights.normal};
`;

export const SignatureContainer = styled.div`
  margin-bottom: 28px;
`;

export const SignatureLabel = styled.h3`
  display: none; /* Title is now inside the signature component */
`;

export const SubmitSection = styled.div`
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

export const ValidationMessage = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.danger};
  margin-top: 12px;
  font-size: 0.875rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

// ============ Success Screen ============

export const SuccessContainer = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding-top: 60px;
  animation: ${slideUp} 0.4s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-top: 32px;
  }
`;

export const SuccessContent = styled.div`
  text-align: center;
`;

export const SuccessAnimation = styled.div`
  width: 72px;
  height: 72px;
  background: ${({ theme }) => theme.colors.successLight};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  animation: ${scaleIn} 0.4s ease;
`;

export const SuccessCheckmark = styled.span`
  color: ${({ theme }) => theme.colors.success};
  font-size: 36px;
`;

export const SuccessDetails = styled.div`
  margin-top: 24px;
  padding: 20px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-align: right;

  p {
    margin: 10px 0;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 0.9375rem;

    strong {
      color: ${({ theme }) => theme.colors.text};
    }
  }
`;

export const HashCode = styled.code`
  display: block;
  margin-top: 8px;
  padding: 10px;
  background: #f8fafc;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  word-break: break-all;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-family: "Roboto Mono", monospace;
`;

export const EmailNotice = styled.div`
  margin-top: 24px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.successLight};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid #a8dab5;

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.successDark};
    font-size: 0.9375rem;
    line-height: 1.5;
  }
`;

// ============ Expired Screen ============

export const ExpiredContainer = styled.div`
  max-width: 440px;
  margin: 0 auto;
  padding-top: 60px;
  animation: ${slideUp} 0.4s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-top: 32px;
  }
`;

export const ExpiredCard = styled.div`
  background: #f8fafc;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 40px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const ExpiredIcon = styled.div`
  width: 56px;
  height: 56px;
  background: ${({ theme }) => theme.colors.warningLight};
  color: ${({ theme }) => theme.colors.warning};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 20px;
`;

export const ExpiredInfoBox = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.colors.warningLight};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  text-align: right;
  border: 1px solid #feefc3;
  margin-top: 20px;

  p {
    margin: 0 0 10px 0;
    color: #b06000;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    font-size: 0.875rem;
  }

  ul {
    margin: 0;
    padding-right: 20px;
    color: #8b5a00;

    li {
      margin-bottom: 6px;
      line-height: 1.5;
      font-size: 0.875rem;
    }
  }
`;

export const ExpiredNote = styled.p`
  margin-top: 20px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
`;
